import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '@centraoneplus/ui';
import {
  BillingCycle,
  EffectivePriceRow,
  PlanTier,
  useRequestPlanChange,
  useValidatePromoCode,
} from './hooks';

const TIER_LABELS: Record<PlanTier, string> = {
  free: 'Free',
  growth: 'Growth',
  professional: 'Professional',
};

interface Props {
  tenantId: string;
  fromPlan: PlanTier;
  toPlan: PlanTier;
  priceRow: EffectivePriceRow | null;
  taxMode: 'inclusive' | 'exclusive' | 'disabled';
  /**
   * Annual discount (0-90) sourced from `pricingConfig.annualDiscountPercent`.
   * Drives the default-cycle rule (YEARLY when there is a real saving) and
   * the "save Z%" copy. Never hardcoded; always passed by the caller.
   */
  annualDiscountPercent: number;
  onClose: () => void;
}

/**
 * Confirmation modal for a plan change request.
 *
 * Owner-only caller. Shows the estimated cost + impact + obligations
 * (for upgrades: "you'll unlock…"; for downgrades: "you'll lose
 * access to…"). Submitting creates the PENDING request; the modal
 * closes immediately and the banner on the parent PlanCard renders
 * the pending state.
 */
export function PlanChangeRequestModal({
  tenantId,
  fromPlan,
  toPlan,
  priceRow,
  taxMode,
  annualDiscountPercent,
  onClose,
}: Props) {
  const request = useRequestPlanChange(tenantId);
  const validatePromo = useValidatePromoCode(tenantId);
  const toast = useToast();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState<null | {
    code: string;
    kind: string;
    percentOff: number | null;
    description: string | null;
  }>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Detect cycle availability from the price row. Pricing is shipped
  // server-side as { monthlyAmount, annualAmount }; a `0`/`0.00` annual
  // amount means SuperAdmin has not configured yearly pricing for this
  // tier, so we should disable the YEARLY cycle for the requester.
  // Same fail-open principle as everywhere else on the Plan & Billing
  // surface: never hardcode prices/discounts on the client.
  const yearlyAvailable = useMemo(() => {
    if (!priceRow?.annualAmount) return false;
    const n = Number(priceRow.annualAmount);
    return Number.isFinite(n) && n > 0;
  }, [priceRow]);
  const monthlyAvailable = useMemo(() => {
    if (!priceRow?.monthlyAmount) return false;
    const n = Number(priceRow.monthlyAmount);
    return Number.isFinite(n) && n > 0;
  }, [priceRow]);

  // Default selection: YEARLY when (a) yearly is configured AND (b) the
  // catalog actually advertises a discount worth highlighting; otherwise
  // MONTHLY (provided MONTHLY is configured). When only one cycle is
  // configured for this tier, we pick that one — the disabled state on
  // the other button explains why the user can't switch.
  const defaultCycle: BillingCycle = yearlyAvailable
    ? !monthlyAvailable || annualDiscountPercent > 0
      ? 'YEARLY'
      : 'MONTHLY'
    : 'MONTHLY';
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(defaultCycle);
  // Submit is blocked when the currently selected cycle is unconfigured —
  // shouldn't happen given the default rule, but a SuperAdmin removing a
  // price after the modal opened is possible.
  const submitDisabled =
    request.isPending ||
    (billingCycle === 'YEARLY' ? !yearlyAvailable : !monthlyAvailable);

  const titleId = useId();
  const descriptionId = useId();
  const promoInputId = useId();
  const cycleGroupId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Focus the dialog on mount, restore the caller's focus on unmount, close on Esc.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [onClose]);

  const isUpgrade = PLAN_INDEX[toPlan] > PLAN_INDEX[fromPlan];

  async function handleApplyPromo() {
    setPromoError(null);
    setPromoValid(null);
    if (!promoCode.trim()) return;
    try {
      const v = await validatePromo.mutateAsync({
        code: promoCode.trim(),
        targetPlan: toPlan,
      });
      setPromoValid({
        code: v.code,
        kind: v.kind,
        percentOff: v.percentOff,
        description: v.description,
      });
    } catch (e: any) {
      setPromoError(
        e?.response?.data?.message ||
          e?.message ||
          'Invalid or expired promo code',
      );
    }
  }

  async function onSubmit() {
    setSubmitError(null);
    try {
      await request.mutateAsync({ fromPlan, toPlan, billingCycle });
      toast.success(
        isUpgrade ? 'Upgrade requested' : 'Downgrade requested',
        `Your ${TIER_LABELS[fromPlan]} → ${TIER_LABELS[toPlan]} (${billingCycle === 'YEARLY' ? 'yearly' : 'monthly'} billing) request was submitted for review. We'll email you when it's approved.`,
      );
      onClose();
    } catch (e: any) {
      // Map known server-side errors to actionable messages. The
      // tenant-service throws BadRequestException("already on plan…") and
      // ConflictException("…already has a pending plan-change request") —
      // surface the server message verbatim so the user sees the precise
      // reason rather than a generic "something went wrong".
      const status = e?.response?.status;
      const serverMessage = e?.response?.data?.message;
      let message: string;
      if (status === 409) {
        message =
          serverMessage ||
          'You already have a pending plan-change request. Cancel it before submitting a new one.';
      } else if (status === 400) {
        message = serverMessage || 'This plan change is not allowed.';
      } else if (status === 403) {
        message =
          'You do not have permission to request a plan change. Ask an organization admin or owner.';
      } else if (status === 404) {
        message =
          'This plan is not yet configured. Please contact our team to enable it.';
      } else {
        message =
          serverMessage ||
          e?.message ||
          'Failed to create plan-change request';
      }
      setSubmitError(message);
      toast.error('Request failed', message);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 id={titleId} className="text-lg font-semibold">
            {isUpgrade ? 'Request upgrade' : 'Request downgrade'}:{' '}
            {TIER_LABELS[fromPlan]} → {TIER_LABELS[toPlan]}
          </h2>
          <p id={descriptionId} className="text-sm text-gray-600 mt-1">
            This request will be reviewed by the CentraOne platform team.
            You'll receive an email once it's approved. Your plan does not
            change until approval.
          </p>
        </header>

        {priceRow && (
          <div className="space-y-2">
            <div
              role="radiogroup"
              aria-labelledby={cycleGroupId}
              className="flex flex-col gap-2"
            >
              <span
                id={cycleGroupId}
                className="text-sm font-medium text-gray-700"
              >
                Billing cycle
              </span>
              <div className="grid grid-cols-2 gap-2">
                <CycleOption
                  cycle="MONTHLY"
                  selected={billingCycle === 'MONTHLY'}
                  disabled={!monthlyAvailable}
                  onSelect={() => setBillingCycle('MONTHLY')}
                  label="Monthly"
                  priceLine={
                    monthlyAvailable
                      ? `${priceRow.currency} ${priceRow.monthlyAmount}/mo`
                      : 'Not available'
                  }
                  hint={
                    monthlyAvailable
                      ? 'Billed each month'
                      : 'No monthly price configured'
                  }
                />
                <CycleOption
                  cycle="YEARLY"
                  selected={billingCycle === 'YEARLY'}
                  disabled={!yearlyAvailable}
                  onSelect={() => setBillingCycle('YEARLY')}
                  label="Yearly"
                  priceLine={
                    yearlyAvailable
                      ? `${priceRow.currency} ${priceRow.annualAmount}/yr`
                      : 'Not available'
                  }
                  hint={
                    yearlyAvailable
                      ? annualDiscountPercent > 0
                        ? `Billed once a year — save ${annualDiscountPercent}%`
                        : 'Billed once a year'
                      : 'No yearly price configured'
                  }
                  badge={
                    yearlyAvailable && annualDiscountPercent > 0
                      ? `Save ${annualDiscountPercent}%`
                      : null
                  }
                />
              </div>
              {taxMode === 'exclusive' && (
                <p className="text-xs text-gray-500">
                  Prices shown are exclusive of applicable tax.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="text-sm">
          {isUpgrade ? (
            <>
              <div className="font-medium mb-1">What you'll unlock</div>
              <ul className="list-disc pl-5 space-y-1 text-gray-700">
                {UNLOCK_SUMMARY[toPlan].map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <div className="font-medium mb-1 text-amber-900">
                What you'll lose access to
              </div>
              <ul className="list-disc pl-5 space-y-1 text-amber-900">
                {LOSE_SUMMARY[fromPlan].map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Your Org Brain graph data is retained for 90 days after downgrade,
                so re-upgrading within that window restores everything without
                re-indexing.
              </p>
            </>
          )}
        </div>

        {isUpgrade && (
          <div className="border-t pt-3 space-y-2">
            <label
              htmlFor={promoInputId}
              className="block text-sm font-medium text-gray-700"
            >
              Promo code (optional)
            </label>
            <div className="flex gap-2">
              <input
                id={promoInputId}
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoValid(null);
                  setPromoError(null);
                }}
                placeholder="e.g. LAUNCH50"
                className="flex-1 border rounded px-3 py-2 text-sm font-mono"
                aria-invalid={Boolean(promoError) || undefined}
                aria-describedby={
                  promoValid
                    ? `${promoInputId}-accepted`
                    : promoError
                      ? `${promoInputId}-error`
                      : undefined
                }
              />
              <Button
                variant="outline"
                onClick={handleApplyPromo}
                disabled={!promoCode.trim() || validatePromo.isPending}
              >
                {validatePromo.isPending ? 'Checking…' : 'Apply'}
              </Button>
            </div>
            {promoValid && (
              <div
                id={`${promoInputId}-accepted`}
                role="status"
                className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2"
              >
                <span className="font-medium">Code accepted:</span>{' '}
                {promoValid.description ||
                  (promoValid.kind === 'percent_off' && promoValid.percentOff
                    ? `${promoValid.percentOff}% off`
                    : promoValid.kind)}
                . The discount will be applied when a platform admin approves
                your request.
              </div>
            )}
            {promoError && (
              <div
                id={`${promoInputId}-error`}
                role="alert"
                className="text-xs text-red-700 bg-red-50 rounded px-3 py-2"
              >
                {promoError}
              </div>
            )}
          </div>
        )}

        {submitError && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
            {submitError}
          </div>
        )}

        <footer className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={request.isPending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={submitDisabled}>
            {request.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function CycleOption({
  cycle,
  selected,
  disabled,
  onSelect,
  label,
  priceLine,
  hint,
  badge,
}: {
  cycle: BillingCycle;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  label: string;
  priceLine: string;
  hint: string;
  badge?: string | null;
}) {
  // role="radio" gives keyboard + screen-reader semantics matching the
  // sibling segmented controls used elsewhere in Settings (e.g.
  // BusinessHours day toggle). Disabled options are still focusable so
  // the user can read why they're unavailable via the `hint` text.
  const base =
    'relative w-full text-left rounded border px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
  const stateClass = disabled
    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
    : selected
      ? 'bg-blue-50 border-blue-500 text-gray-900 ring-1 ring-blue-500'
      : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      data-cycle={cycle}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      className={`${base} ${stateClass}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{label}</span>
        {badge && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-1 text-sm font-semibold">{priceLine}</div>
      <div className="mt-0.5 text-xs text-gray-500">{hint}</div>
    </button>
  );
}

const PLAN_INDEX: Record<PlanTier, number> = {
  free: 0,
  growth: 1,
  professional: 2,
};

const UNLOCK_SUMMARY: Record<PlanTier, string[]> = {
  free: [],
  growth: [
    'Chat, WhatsApp, SMS channels',
    'AI intent classification, sentiment, response suggestions',
    'Up to 10 automation rules + webhooks',
    'Bulk operations, API keys',
  ],
  professional: [
    'Voice, Twitter/X, Facebook, Instagram, Slack channels',
    'Org Brain — Admin Q&A, impact analysis, config health',
    'Advanced AI — chatbot, voice bot, agent assist, MCP tools, LLM extraction',
    'Unlimited automation rules, n8n integration, scheduled jobs',
    'Custom reports, predictions, NL queries, causal analytics',
    'SAML SSO, IP restrictions, GDPR tools, audit export',
    'Multi-brand + custom branding',
  ],
};

const LOSE_SUMMARY: Record<PlanTier, string[]> = {
  free: [],
  growth: [
    'All Growth-tier features (chat, WhatsApp, SMS, basic AI)',
    'Automation rules, webhooks, bulk operations',
    'Your ticket quota drops to 100/month',
  ],
  professional: [
    'Voice, social channels, Slack',
    'Org Brain (graph retained for 90 days)',
    'Advanced AI, n8n, scheduled jobs',
    'Custom reports, predictions, NL queries',
    'SAML SSO, IP restrictions, audit export',
  ],
};
